import React, { useCallback, useMemo } from 'react';
import { Select, Space, Table, Tooltip, TableColumnsType, Popover } from 'antd';
import { useGateValue } from '@statsig/react-bindings';
import { useManualShipmentCreationFlags } from '@/hooks/useManualShipmentCreationFlags';
import { PermissionValue, RoleModel, FeaturesRow } from './utils/types';
import { CustomTitle } from '@/components';
import ArrowDown from '@/assets/icons/arrow-down-role.svg';
import { PencilIcon } from '@/assets/icons/icons';
import { getBackground } from './utils/permissions';
import { FeaturePermissionMatrix } from '@/apis/types';
import KpiChartsSelector, {
  KpiSelectionPayload,
  isKpiParentOnly,
} from './KpiChartSelector';
import type {
  AlertThreshold,
  SelectedThresholdValues,
} from '@/pages/Shipments/PerShipmentNotification/components/AlertFieldByType';
import ShipmentAlertThresholds from './ShipmentAlertThresholds';
import ShipmentStatusPopover from './ShipmentPopover';
import './RolesAndPermissions.css';

const { Option } = Select;

type KpiItem = { id: string; label: string };
type KpiGroup = { id: string; label: string; items: KpiItem[] };

interface Props {
  rows: FeaturesRow[] | null;
  roles: RoleModel[];
  isBaseLocked: (roleName: string) => boolean;
  optionsForRow: (
    featureName: string
  ) => { value: PermissionValue; label: string }[];
  onChange: (
    featureName: string,
    role: RoleModel,
    val: PermissionValue
  ) => void;
  onEditRole: (role: RoleModel) => void;
  canEditRoleHeader: (index: number) => boolean;

  onOpenStatus: (role: RoleModel) => void;
  allStatuses: string[] | null;
  statusOpenRoleId: string | null;
  statusTempSelected: string[];
  setStatusTempSelected: (vals: string[]) => void;
  onStatusReset: () => void;
  onStatusApply: () => void;
  onStatusClose: () => void;

  shipmentStatusSelectAllDefault?: boolean;

  alertThreshold: AlertThreshold[] | undefined;
  alertsOpenRoleId: string | null;
  alertsTempSelected: SelectedThresholdValues;
  setAlertsTempSelected: (
    value:
      | SelectedThresholdValues
      | ((prev: SelectedThresholdValues) => SelectedThresholdValues)
  ) => void;
  selectAllAlertTypes: boolean;
  setSelectAllAlertTypes: (val: boolean) => void;
  onOpenAlerts: (role: RoleModel) => void;
  onAlertsReset: () => void;
  onAlertsApply: () => void;
  onAlertsClose: () => void;

  kpiOpenRoleId: string | null;
  kpiTempSelected: string[];
  setKpiTempSelected: (vals: string[]) => void;
  selectAllKpis: boolean;
  setSelectAllKpis: (val: boolean) => void;
  kpiTotalCount: number;
  setKpiTotalCount: (n: number) => void;
  onOpenKpis: (role: RoleModel) => void;
  onKpisReset: () => void;
  onKpisApply: (idsOverride?: string[]) => void;
  onKpisClose: () => void;

  kpiGroups?: KpiGroup[];
  kpiSelectAllDefault?: boolean;
  alertsReady: boolean;
  isDesktop: boolean;
  permission: FeaturePermissionMatrix;

  kpiReportsLabels?: string[];
  kpiReportsOpenRoleId: string | null;
  kpiReportsTempSelected: string[];
  setKpiReportsTempSelected: (vals: string[]) => void;
  onOpenKpiReports: (role: RoleModel) => void;
  onKpiReportsReset: () => void;
  onKpiReportsApply: () => void;
  onKpiReportsClose: () => void;
  kpiReportsSelectAllDefault?: boolean;
}

export const ASSIGN_ALERTS_OFF_TOOLTIP =
  'Turn on alert owner assignment in Alert settings to configure.';

const FIRST_COL_W = 210;
const COL_W = 180;

const FeaturesTable: React.FC<Props> = (props) => {
  const {
    alertsReady,
    isDesktop,
    rows,
    roles,
    isBaseLocked,
    optionsForRow,
    onChange,
    onEditRole,
    canEditRoleHeader,

    onOpenStatus,
    allStatuses,
    statusOpenRoleId,
    statusTempSelected,
    setStatusTempSelected,
    onStatusReset,
    onStatusApply,
    onStatusClose,

    shipmentStatusSelectAllDefault = false,

    alertThreshold,
    alertsOpenRoleId,
    alertsTempSelected,
    setAlertsTempSelected,
    selectAllAlertTypes,
    setSelectAllAlertTypes,
    onOpenAlerts,
    onAlertsReset,
    onAlertsApply,
    onAlertsClose,

    kpiOpenRoleId,
    setKpiTempSelected,
    setSelectAllKpis,
    setKpiTotalCount,
    onOpenKpis,
    onKpisApply,
    onKpisClose,

    kpiGroups = [],
    kpiSelectAllDefault = true,

    permission,

    kpiReportsLabels = [],
    kpiReportsOpenRoleId,
    kpiReportsTempSelected,
    setKpiReportsTempSelected,
    onOpenKpiReports,
    onKpiReportsReset,
    onKpiReportsApply,
    onKpiReportsClose,
    kpiReportsSelectAllDefault = false,
  } = props;

  // "Assign alerts" only means something once the company has owner assignment
  // turned on, so its row is greyed out until then. Only with the
  // alert_assignment gate on (otherwise the setting cannot be turned on at all),
  // and only once the setting is known.
  const alertAssignmentGate = useGateValue('alert_assignment');
  const { ownerAssignmentEnabled, ownerAssignmentKnown } =
    useManualShipmentCreationFlags();
  const assignAlertsSwitchedOff =
    alertAssignmentGate && ownerAssignmentKnown && !ownerAssignmentEnabled;

  const data = rows ?? [];
  const statuses = useMemo(() => allStatuses ?? [], [allStatuses]);
  const totalAlertCount =
    alertThreshold?.reduce((acc, g) => acc + (g.alerts?.length || 0), 0) ?? 0;
  const kpiTotalSelectable = useMemo(() => {
    const parentCount = kpiGroups.filter(isKpiParentOnly).length;

    const itemCount = kpiGroups.reduce(
      (acc, g) => acc + (g.items?.length ?? 0),
      0
    );

    return parentCount + itemCount;
  }, [kpiGroups]);

  const handleKpiApply = useCallback(
    (payload: KpiSelectionPayload) => {
      const ids = payload.allowed_statuses ?? [];

      setKpiTempSelected(ids);
      setKpiTotalCount(kpiTotalSelectable);
      setSelectAllKpis(
        kpiTotalSelectable > 0 && ids.length === kpiTotalSelectable
      );

      onKpisApply(ids);
    },
    [
      onKpisApply,
      setKpiTempSelected,
      setKpiTotalCount,
      setSelectAllKpis,
      kpiTotalSelectable,
    ]
  );

  const handleKpiReset = useCallback(() => {
    const defaultIds = kpiSelectAllDefault
      ? [
          ...kpiGroups.filter(isKpiParentOnly).map((g) => g.id),
          ...kpiGroups.flatMap((g) => (g.items ?? []).map((i) => i.id)),
        ]
      : [];

    setKpiTempSelected(defaultIds);
    setSelectAllKpis(kpiSelectAllDefault && defaultIds.length > 0);

    onKpisApply(defaultIds);
  }, [
    kpiGroups,
    kpiSelectAllDefault,
    onKpisApply,
    setKpiTempSelected,
    setSelectAllKpis,
  ]);

  const baseCol: TableColumnsType<FeaturesRow>[number] = useMemo(
    () => ({
      title: '',
      dataIndex: 'feature_name',
      key: 'feature_name',
      fixed: isDesktop ? 'left' : undefined,
      width: FIRST_COL_W,
      minWidth: FIRST_COL_W,
      render: (_v, record) => (
        <div className='ft-featureCell'>
          <CustomTitle
            text={record.feature_name}
            lineHeight={'12px'}
            fontWeight={600}
            fontSize={12}
            color='var(--text-primary)'
            marginBottom={0}
          />
          {record?.description ? (
            <CustomTitle
              text={record.description}
              level={5}
              fontSize={10}
              lineHeight='14px'
              color='var(--text-light)'
              fontWeight={400}
              marginTop={0}
            />
          ) : null}
        </div>
      ),
    }),
    [isDesktop]
  );

  const roleCols: TableColumnsType<FeaturesRow> = useMemo(
    () =>
      roles.map((role, idx) => ({
        dataIndex: role.id,
        key: role.id,
        width: COL_W,
        minWidth: COL_W,
        onHeaderCell: () => ({ style: { minWidth: COL_W } }),
        onCell: () => ({ style: { minWidth: COL_W } }),
        title: (
          <div className='ft-roleHeader'>
            <span className='ft-roleName'>{role.name}</span>
            {canEditRoleHeader(idx) && (
              <Tooltip title={`Edit ${role.name}`}>
                <span
                  role='button'
                  aria-label='edit-role'
                  className='ft-editHeaderIcon'
                  onClick={() => onEditRole(role)}
                >
                  <PencilIcon
                    width={12}
                    height={12}
                    color='var(--text-primary)'
                  />
                </span>
              </Tooltip>
            )}
          </div>
        ),
        render: (val: PermissionValue, record: FeaturesRow) => {
          const hasWrite = Boolean(
            permission?.is_update || permission?.is_create
          );
          const featureLower = record.feature_name.toLowerCase();
          const switchedOff =
            featureLower === 'assign alerts' && assignAlertsSwitchedOff;
          const locked = isBaseLocked(role.name) || !hasWrite || switchedOff;
          const opts = optionsForRow(record.feature_name);
          const isShipment = featureLower === 'shipment status';
          const isAlerts = featureLower === 'alerts';
          const isHomeKpis = featureLower === 'home kpi charts';
          const isHomeKpiReports = featureLower === 'home kpi reports';
          const allowed = record?.allowedMap?.[role.id];
          const isNoAccess = val === 'NO_ACCESS';

          const persistedAlertsCount =
            allowed.length === 0 ? 0 : allowed.length;
          const persistedKpiCount =
            allowed === undefined
              ? kpiSelectAllDefault
                ? kpiTotalSelectable
                : 0
              : allowed.length;

          const selectStyle: React.CSSProperties = {
            width: '100%',
            background: getBackground(
              role.name,
              isNoAccess,
              isBaseLocked,
              permission
            ),
            border:
              !permission.is_create && !permission.is_update
                ? '1px solid var(--gray-600)'
                : !isBaseLocked(role.name)
                  ? 'none'
                  : '1px solid var(--gray-600)',
            fontSize: 12,
            borderRadius: 4,
            cursor: locked ? 'not-allowed' : 'pointer',
          };

          const permissionSelect = (
            <Select
              disabled={locked}
              suffixIcon={<img src={ArrowDown} className='ft-arrowIcon' />}
              className={`ft-select ${!permission.is_create && !permission.is_update ? 'black-border' : !isBaseLocked(role.name) ? 'b-none' : 'black-border'}`}
              style={selectStyle}
              value={val}
              onChange={(v) => onChange(record.feature_name, role, v)}
            >
              {opts.map((o) => (
                <Option key={String(o.value)} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          );

          return (
            <div className='ft-cellWrap font-black-color section-table'>
              {switchedOff ? (
                // A disabled select swallows hover, so the tooltip sits on a wrapper.
                <Tooltip title={ASSIGN_ALERTS_OFF_TOOLTIP}>
                  <span className='ft-switchedOff'>{permissionSelect}</span>
                </Tooltip>
              ) : (
                permissionSelect
              )}

              {isShipment && (
                <ShipmentStatusPopover
                  disabled={isNoAccess}
                  open={statusOpenRoleId === role.id}
                  selectedCount={allowed.length}
                  totalCount={statuses.length}
                  statuses={statuses}
                  value={statusTempSelected}
                  onChangeValue={setStatusTempSelected}
                  onEditClick={() => onOpenStatus(role)}
                  onClose={onStatusClose}
                  onReset={onStatusReset}
                  onApply={onStatusApply}
                  placement='bottomLeft'
                  selectAllByDefault={shipmentStatusSelectAllDefault}
                  title='Viewable shipment statuses'
                />
              )}

              {isAlerts && (
                <Space size={6}>
                  <span className='ft-count'>
                    {persistedAlertsCount}/{totalAlertCount}
                  </span>

                  <Popover
                    open={
                      alertsReady && !isNoAccess && alertsOpenRoleId === role.id
                    }
                    trigger='click'
                    onOpenChange={(next) => {
                      if (!next) onAlertsClose();
                    }}
                    placement='bottomLeft'
                    getPopupContainer={() => document.body}
                    overlayStyle={{ zIndex: 2000 }}
                    overlayInnerStyle={{
                      borderRadius: 12,
                      padding: 0,
                      width: 380,
                    }}
                    content={
                      <div>
                        <ShipmentAlertThresholds
                          shipment={{}}
                          onAlertsReset={onAlertsReset}
                          onAlertsApply={onAlertsApply}
                          alertThreshold={alertThreshold ?? []}
                          selectedThresholdValues={alertsTempSelected}
                          setSelectedThresholdValues={setAlertsTempSelected}
                          selectAllAlertTypes={selectAllAlertTypes}
                          setSelectAllAlertTypes={setSelectAllAlertTypes}
                          maxHeight='500px'
                          enableAllThresholds
                        />
                      </div>
                    }
                  >
                    <div
                      role='button'
                      aria-label='edit-alerts'
                      className={`ft-editCircle ${
                        isNoAccess || !alertsReady ? 'isDisabled' : ''
                      }`}
                      aria-disabled={isNoAccess || !alertsReady}
                      onClick={() => {
                        if (!isNoAccess && alertsReady) onOpenAlerts(role);
                      }}
                    >
                      <PencilIcon
                        width={14}
                        height={14}
                        color='var(--text-primary)'
                      />
                    </div>
                  </Popover>
                </Space>
              )}

              {isHomeKpis && (
                <Space size={6}>
                  <span className='ft-count'>
                    {persistedKpiCount}/{kpiTotalSelectable || 0}
                  </span>

                  <Popover
                    open={!isNoAccess && kpiOpenRoleId === role.id}
                    trigger='click'
                    onOpenChange={(next) => {
                      if (!next) onKpisClose();
                    }}
                    placement='bottomLeft'
                    getPopupContainer={() => document.body}
                    overlayStyle={{ zIndex: 2000 }}
                    overlayInnerStyle={{
                      borderRadius: 12,
                      padding: 0,
                      width: 380,
                    }}
                    content={
                      <KpiChartsSelector
                        groups={kpiGroups}
                        selectAllByDefault={kpiSelectAllDefault}
                        initialSelectedIds={
                          allowed === undefined ? undefined : allowed
                        }
                        maxHeight='500px'
                        onApply={handleKpiApply}
                        onReset={handleKpiReset}
                      />
                    }
                  >
                    <div
                      role='button'
                      aria-label='edit-kpis'
                      className={`ft-editCircle ${
                        isNoAccess ? 'isDisabled' : ''
                      }`}
                      aria-disabled={isNoAccess}
                      onClick={() => {
                        if (!isNoAccess) onOpenKpis(role);
                      }}
                    >
                      <PencilIcon
                        width={14}
                        height={14}
                        color='var(--text-primary)'
                      />
                    </div>
                  </Popover>
                </Space>
              )}

              {isHomeKpiReports && (
                <ShipmentStatusPopover
                  disabled={isNoAccess}
                  open={kpiReportsOpenRoleId === role.id}
                  selectedCount={allowed.length}
                  totalCount={kpiReportsLabels.length}
                  statuses={kpiReportsLabels}
                  value={kpiReportsTempSelected}
                  onChangeValue={setKpiReportsTempSelected}
                  onEditClick={() => onOpenKpiReports(role)}
                  onClose={onKpiReportsClose}
                  onReset={onKpiReportsReset}
                  onApply={onKpiReportsApply}
                  placement='bottomLeft'
                  selectAllByDefault={kpiReportsSelectAllDefault}
                  title='Viewable Home KPI reports'
                />
              )}
            </div>
          );
        },
      })),
    [
      roles,
      canEditRoleHeader,
      onEditRole,
      permission,
      isBaseLocked,
      optionsForRow,
      totalAlertCount,
      kpiSelectAllDefault,
      statusOpenRoleId,
      statuses,
      statusTempSelected,
      setStatusTempSelected,
      onStatusClose,
      onStatusReset,
      onStatusApply,
      shipmentStatusSelectAllDefault,
      alertsReady,
      alertsOpenRoleId,
      onAlertsReset,
      onAlertsApply,
      alertThreshold,
      alertsTempSelected,
      setAlertsTempSelected,
      selectAllAlertTypes,
      setSelectAllAlertTypes,
      kpiTotalSelectable,
      kpiOpenRoleId,
      kpiGroups,
      handleKpiApply,
      handleKpiReset,
      onChange,
      onOpenStatus,
      onAlertsClose,
      onOpenAlerts,
      onKpisClose,
      onOpenKpis,
      kpiReportsLabels,
      kpiReportsOpenRoleId,
      kpiReportsTempSelected,
      setKpiReportsTempSelected,
      onOpenKpiReports,
      onKpiReportsReset,
      onKpiReportsApply,
      onKpiReportsClose,
      kpiReportsSelectAllDefault,
      assignAlertsSwitchedOff,
    ]
  );

  const columns: TableColumnsType<FeaturesRow> = useMemo(
    () => [baseCol, ...roleCols],
    [baseCol, roleCols]
  );

  const totalWidth = FIRST_COL_W + roles.length * COL_W;

  return (
    <Table<FeaturesRow>
      columns={columns}
      dataSource={data}
      pagination={false}
      className='custom-roles-table w-full'
      scroll={{ x: totalWidth }}
      sticky
      tableLayout='fixed'
      rowKey='key'
    />
  );
};

export default FeaturesTable;
